import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { resolveDir, toPx, type Dir } from '../components/base/primitiveUtils';

/**
 * Places a floating panel directly BELOW its anchor, in viewport coordinates
 * (`position: fixed`), so no `overflow: hidden` ancestor can clip it.
 *
 * It never flips upward. A native <select> near the bottom of the screen opens up,
 * which is exactly what the owner reported on the home hero; here, when there is not
 * enough room below, the page (or the anchor's own scroll container) is scrolled by
 * the shortfall instead, once, right after opening.
 *
 * Horizontal alignment is logical: `align="start"` lines the panel up with the
 * anchor's inline-start edge, which is its RIGHT edge in Arabic.
 */

export type AnchoredWidth = 'anchor' | number | string;

export interface UseAnchoredPositionOptions {
  open: boolean;
  /** 'anchor' matches the anchor's width; a number is px; a string is any CSS length. */
  width?: AnchoredWidth;
  /** Lower bound for the panel width (px number or '<n>rem'). */
  minWidth?: number | string;
  align?: 'start' | 'end';
  /** Gap between anchor and panel, px. */
  offset?: number;
  /** Room (px) the panel wants below the anchor before the page is scrolled to make it. */
  minSpaceBelow?: number;
  /** Scroll to make room below. Default true. */
  scrollIntoRoom?: boolean;
  /** Scrolls the window; lets the caller route through Lenis. Defaults to window.scrollBy. */
  scrollWindowBy?: (amount: number, smooth: boolean) => void;
  /** Jump instead of animating the make-room scroll. */
  reducedMotion?: boolean;
}

export interface AnchoredPosition {
  /** Spread onto the floating element. */
  style: CSSProperties;
  /** Direction resolved from the anchor; set it on the portalled panel. */
  dir: Dir;
  /** False until the first measurement; the panel is invisible until then. */
  ready: boolean;
  /** Re-measure now (after content changes the caller knows about). */
  update: () => void;
}

const EDGE = 8; // keep the panel this far from the viewport's side edges
const BOTTOM_GAP = 12; // and this far from its bottom edge
const TOP_GUARD_REM = 5; // never scroll the anchor up under the sticky header
const AUTO_SCROLL_WINDOW_MS = 700; // only make room right after opening

interface Placement {
  top: number;
  left: number;
  width?: number;
  maxHeight: number;
  dir: Dir;
}

function viewportSize() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
  const width = document.documentElement.clientWidth || window.innerWidth;
  const bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
  return { width, bottom };
}

function findScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
}

function defaultScrollWindowBy(amount: number, smooth: boolean) {
  window.scrollBy({ top: amount, behavior: smooth ? 'smooth' : 'auto' });
}

export function useAnchoredPosition(
  anchorRef: RefObject<HTMLElement | null>,
  floatingRef: RefObject<HTMLElement | null>,
  {
    open,
    width = 'anchor',
    minWidth,
    align = 'start',
    offset = 8,
    minSpaceBelow = 240,
    scrollIntoRoom = true,
    scrollWindowBy = defaultScrollWindowBy,
    reducedMotion = false,
  }: UseAnchoredPositionOptions,
): AnchoredPosition {
  const [placement, setPlacement] = useState<Placement | null>(null);
  const openedAtRef = useRef(0);
  const scrolledRef = useRef(false);
  const frameRef = useRef(0);

  // Options read inside listeners; refreshed every render.
  const optsRef = useRef({ width, minWidth, align, offset, minSpaceBelow, scrollIntoRoom, scrollWindowBy, reducedMotion });
  optsRef.current = { width, minWidth, align, offset, minSpaceBelow, scrollIntoRoom, scrollWindowBy, reducedMotion };

  const makeRoom = useCallback((anchor: HTMLElement, rect: DOMRect, available: number) => {
    const o = optsRef.current;
    if (!o.scrollIntoRoom || scrolledRef.current) return;
    if (Date.now() - openedAtRef.current > AUTO_SCROLL_WINDOW_MS) return;
    const floating = floatingRef.current;
    // Content shorter than the minimum needs only its own height.
    const natural = floating ? floating.scrollHeight : o.minSpaceBelow;
    const needed = Math.min(natural, o.minSpaceBelow);
    let shortfall = Math.ceil(needed - available);
    if (shortfall <= 1) return;
    // Never push the anchor itself up under the sticky header.
    const guard = toPx(`${TOP_GUARD_REM}rem`) ?? 80;
    shortfall = Math.min(shortfall, Math.max(0, rect.top - guard));
    if (shortfall <= 1) return;

    scrolledRef.current = true;
    const smooth = !o.reducedMotion;
    let remaining = shortfall;
    const container = findScrollParent(anchor);
    if (container) {
      const room = container.scrollHeight - container.clientHeight - container.scrollTop;
      const step = Math.min(remaining, Math.max(0, room));
      if (step > 0) {
        container.scrollBy({ top: step, behavior: smooth ? 'smooth' : 'auto' });
        remaining -= step;
      }
    }
    if (remaining > 0) {
      const doc = document.scrollingElement || document.documentElement;
      const room = doc.scrollHeight - window.innerHeight - window.scrollY;
      const step = Math.min(remaining, Math.max(0, room));
      if (step > 0) o.scrollWindowBy(step, smooth);
    }
  }, [floatingRef]);

  const compute = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const o = optsRef.current;
    const rect = anchor.getBoundingClientRect();
    const vp = viewportSize();
    const dir = resolveDir(anchor);
    const maxWidth = Math.max(0, vp.width - EDGE * 2);

    let w: number | undefined;
    if (o.width === 'anchor') w = rect.width;
    else if (typeof o.width === 'number') w = o.width;
    const minW = toPx(o.minWidth);
    if (w !== undefined && minW !== undefined) w = Math.max(w, minW);
    if (w !== undefined) w = Math.min(w, maxWidth);
    // A CSS-length width is applied by the caller; measure what it became.
    const actual = w ?? Math.min(floatingRef.current?.offsetWidth ?? 0, maxWidth);

    // Logical alignment: 'start' hugs the inline-start edge, the RIGHT edge in RTL.
    const hugRight = (o.align === 'start') === (dir === 'rtl');
    let left = hugRight ? rect.right - actual : rect.left;
    left = Math.min(Math.max(left, EDGE), Math.max(EDGE, vp.width - EDGE - actual));

    const top = rect.bottom + o.offset;
    const available = vp.bottom - top - BOTTOM_GAP;
    setPlacement((prev) => {
      const next: Placement = {
        top: Math.round(top),
        left: Math.round(left),
        width: w === undefined ? undefined : Math.round(w),
        maxHeight: Math.max(0, Math.floor(available)),
        dir,
      };
      if (
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.maxHeight === next.maxHeight &&
        prev.dir === next.dir
      ) {
        return prev;
      }
      return next;
    });
    makeRoom(anchor, rect, available);
  }, [anchorRef, floatingRef, makeRoom]);

  const schedule = useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      compute();
    });
  }, [compute]);

  // Placement happens before paint, so the panel never flashes at 0,0. Re-runs when
  // the geometry options change while open.
  const wasOpenRef = useRef(false);
  useLayoutEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      scrolledRef.current = false;
      setPlacement(null);
      return;
    }
    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      openedAtRef.current = Date.now();
      scrolledRef.current = false;
    }
    compute();
  }, [open, width, minWidth, align, offset, compute]);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = (event: Event) => {
      // The panel scrolling its own list does not move it.
      const floating = floatingRef.current;
      if (floating && event.target instanceof Node && floating.contains(event.target)) return;
      schedule();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', schedule);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(schedule);
      if (anchorRef.current) observer.observe(anchorRef.current);
      if (floatingRef.current) observer.observe(floatingRef.current);
    }
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', schedule);
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
      observer?.disconnect();
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = 0;
      }
    };
  }, [open, schedule, anchorRef, floatingRef]);

  const style: CSSProperties = {
    position: 'fixed',
    top: placement?.top ?? 0,
    left: placement?.left ?? 0,
    maxWidth: `calc(100vw - ${EDGE * 2}px)`,
    maxHeight: placement ? placement.maxHeight : undefined,
    visibility: placement ? undefined : 'hidden',
  };
  if (width === 'anchor' || typeof width === 'number') {
    if (placement?.width !== undefined) style.width = placement.width;
  } else {
    style.width = width;
    const minW = toPx(minWidth);
    if (minW !== undefined) style.minWidth = minW;
  }

  return {
    style,
    dir: placement?.dir ?? resolveDir(anchorRef.current),
    ready: placement !== null,
    update: compute,
  };
}

export default useAnchoredPosition;
